import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { ModalWrapper } from '../ui/ModalWrapper';
import { Icons } from '@shared/constants';
import {
  markReviewCompleted,
  markNotEnjoying,
} from '@shared/services/reviewPromptService';
import { recordFeatureUseEvent } from '@shared/services/analyticsService';
import { requestAppReview } from '@/lib/appReview';
import { logError } from '@/lib/log';
import { APP_VERSION } from '../../version';

const HUBSPOT_PORTAL_ID = import.meta.env.VITE_HUBSPOT_PORTAL_ID || '44932186';
const HUBSPOT_FORM_ID = '053c560c-b5a7-4b03-bc0d-430eb74d2436';

function getAppVersionString(): string {
  let platform = 'web';
  try {
    platform = Capacitor.getPlatform();
  } catch {
    /* default web */
  }
  return `${APP_VERSION} (${platform})`;
}

type Step = 'ask' | 'feedback' | 'thanks';

interface EnjoyingEnoughModalProps {
  isOpen: boolean;
  uid: string;
  email: string;
  /** Plain close (no further state change). */
  onClose: () => void;
  /** Closed from the ask step without choosing — snooze and ask again later. */
  onDismiss: () => void;
}

/**
 * Two-step soft prompt:
 *   1. "Are you enjoying enough?"  → Yes triggers the native review sheet,
 *      Not really routes to a private feedback form (no public 1-star).
 *
 * Mounted app-wide by ReviewPromptProvider; only ever opened on native after
 * the daily session completes and eligibility checks pass.
 */
export const EnjoyingEnoughModal: React.FC<EnjoyingEnoughModalProps> = ({
  isOpen,
  uid,
  email,
  onClose,
  onDismiss,
}) => {
  const [step, setStep] = useState<Step>('ask');
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setStep('ask');
    setFeedback('');
    setSubmitting(false);
    setError('');
  };

  // Backdrop / X behaviour depends on where the user is.
  const handleRequestClose = () => {
    if (step === 'ask') {
      onDismiss(); // didn't choose — try again another day
    } else {
      onClose();
    }
    reset();
  };

  const handleYes = async () => {
    // Persist in the background — don't block the UI on a network write.
    markReviewCompleted(uid).catch(logError);
    void recordFeatureUseEvent(uid, 'review_prompt', 'answer_positive', { oncePerSession: false });
    // Fire the OS sheet, then thank them regardless of what the OS did.
    await requestAppReview();
    setStep('thanks');
  };

  const handleNo = () => {
    markNotEnjoying(uid).catch(logError);
    void recordFeatureUseEvent(uid, 'review_prompt', 'answer_negative', { oncePerSession: false });
    setStep('feedback');
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields: [
              { objectTypeId: '0-1', name: 'email', value: email },
              {
                objectTypeId: '0-5',
                name: 'content',
                value: `[App review feedback] ${feedback.trim()}`,
              },
              { objectTypeId: '0-1', name: 'e_app_version', value: getAppVersionString() },
              { objectTypeId: '0-1', name: 'e_contact_error_message', value: '' },
            ],
            context: {
              pageUri: window.location.href,
              pageName: 'App Review Feedback',
            },
          }),
        }
      );
      if (!response.ok) throw new Error('Submission failed');
      setStep('thanks');
    } catch (err) {
      logError(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={handleRequestClose} size="sm" showCloseButton layer="elevated">
      {step === 'ask' && (
        <div className="text-center space-y-5 pt-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-foliage/10 flex items-center justify-center">
            <Icons.Heart className="w-7 h-7 text-foliage" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-serif text-growth">
              Enjoying <span className="font-serif font-italic"><i>enough.</i></span>?
            </h3>
            <p className="text-sm text-growth/70">
              You're making real progress on your financial wellness journey. How's it going?
            </p>
          </div>
          <div className="space-y-3 pt-1">
            <button
              type="button"
              onClick={handleYes}
              className="w-full py-3 bg-growth text-cloud font-medium text-sm rounded-2xl transition-all hover:bg-growth/90 active:scale-[0.98]"
            >
              Yes, I enjoy it.
            </button>
            <button
              type="button"
              onClick={handleNo}
              className="w-full py-3 text-growth/70 font-medium text-sm rounded-2xl transition-colors hover:text-growth"
            >
              Not really
            </button>
          </div>
        </div>
      )}

      {step === 'feedback' && (
        <form onSubmit={handleSubmitFeedback} className="space-y-5 pt-2">
          <div className="space-y-2">
            <h3 className="text-xl font-serif text-growth">What could be better?</h3>
            <p className="text-sm text-growth/70">
              Your comments help make <i>enough.</i> more helpful for everyone.
            </p>
          </div>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Share your thoughts..."
            rows={5}
            maxLength={5000}
            autoFocus
            className="w-full px-4 py-3 bg-sand border border-mist/30 rounded-2xl text-growth text-sm placeholder:text-growth/50 focus:outline-none focus:border-sky/50 focus:ring-1 focus:ring-sky/20 transition-colors resize-none"
          />
          {error && <p className="text-sm text-wildfire">{error}</p>}
          <button
            type="submit"
            disabled={!feedback.trim() || submitting}
            className="w-full py-3 bg-growth text-cloud font-medium text-sm rounded-2xl transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-growth/90 active:scale-[0.98]"
          >
            {submitting ? 'Sending...' : 'Send feedback'}
          </button>
        </form>
      )}

      {step === 'thanks' && (
        <div className="text-center space-y-5 pt-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-foliage/10 flex items-center justify-center">
            <Icons.Check className="w-7 h-7 text-foliage" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-serif text-growth">Thank you</h3>
            <p className="text-sm text-growth/70">
              It means a lot that you're here. Keep going.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              onClose();
              reset();
            }}
            className="w-full py-3 bg-growth text-cloud font-medium text-sm rounded-2xl transition-all hover:bg-growth/90 active:scale-[0.98]"
          >
            Close
          </button>
        </div>
      )}
    </ModalWrapper>
  );
};
